import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
} from '../../../components';

import SocialAuthButtons from '../SocialAuthButtons/SocialAuthButtons';

import css from './WelcomeForm.module.css';

const WelcomeFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        showVippsLogin,
        showFacebookLogin,
        showGoogleLogin,
        onAuthWithVipps,
        onAuthWithFacebook,
        onAuthWithGoogle,
      } = fieldRenderProps;

      const emailLabel = intl.formatMessage({ id: 'WelcomeForm.emailLabel' });
      const emailPlaceholder = intl.formatMessage({ id: 'WelcomeForm.emailPlaceholder' });
      const emailRequiredMessage = intl.formatMessage({ id: 'WelcomeForm.emailRequired' });
      const emailInvalidMessage = intl.formatMessage({ id: 'WelcomeForm.emailInvalid' });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {/* Social buttons sit above the email gateway in the Figma. */}
          <SocialAuthButtons
            vippsLabelId="WelcomeForm.continueWithVipps"
            googleLabelId="WelcomeForm.continueWithGoogle"
            facebookLabelId="WelcomeForm.continueWithFacebook"
            showVippsLogin={showVippsLogin}
            showFacebookLogin={showFacebookLogin}
            showGoogleLogin={showGoogleLogin}
            onAuthWithVipps={onAuthWithVipps}
            onAuthWithFacebook={onAuthWithFacebook}
            onAuthWithGoogle={onAuthWithGoogle}
          />

          <div className={css.divider}>
            <span className={css.dividerText}>
              <FormattedMessage id="WelcomeForm.or" />
            </span>
          </div>

          <FieldTextInput
            type="email"
            id={formId ? `${formId}.email` : 'WelcomeForm.email'}
            name="email"
            autoComplete="email"
            label={emailLabel}
            placeholder={emailPlaceholder}
            validate={validators.composeValidators(emailRequired, emailValid)}
          />

          <PrimaryButton
            type="submit"
            className={css.submit}
            inProgress={submitInProgress}
            disabled={submitDisabled}
          >
            <FormattedMessage id="WelcomeForm.continue" />
          </PrimaryButton>
        </Form>
      );
    }}
  />
);

/**
 * Welcome / email-gateway form rendered on /signup.
 *
 * Layout:
 *  - Vipps + Google + Facebook social buttons (each gated by its own
 *    REACT_APP_*_CLIENT_ID env var).
 *  - "ELLER" divider
 *  - Email input + Continue
 *
 * On submit the parent should route the user to /signup/complete with
 * the email pre-filled — the gateway always assumes new-user intent
 * (per product direction: "always go to signup with email pre-filled;
 * existing users get a clear 'email already taken' error after they
 * submit the full form").
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName]
 * @param {string} [props.className]
 * @param {string} [props.formId]
 * @param {boolean} [props.inProgress]
 * @param {boolean} [props.showVippsLogin]
 * @param {boolean} [props.showFacebookLogin]
 * @param {boolean} [props.showGoogleLogin]
 * @param {Function} [props.onAuthWithVipps]
 * @param {Function} [props.onAuthWithFacebook]
 * @param {Function} [props.onAuthWithGoogle]
 * @returns {JSX.Element}
 */
const WelcomeForm = props => {
  const intl = useIntl();
  return <WelcomeFormComponent {...props} intl={intl} />;
};

export default WelcomeForm;
